/* Key Dates / Shipping / Financial summary cards — mirrors the merchant panel's SummaryCards,
   using the admin order object + admin helpers. Purely presentational. */
import React, { useEffect, useState } from 'react'
import { Grid, Tooltip, Typography } from '@mui/material'
import { formatCurrency, formatDate } from '../../../common/util'
import { parseDateAndTime } from '../../../helpers/UtilityHelper'
import { apiConstants, getGeneric } from '../../../common/constants'
import { SectionCard, InfoRow, T } from './detailKit'
import useB2BLane from './useB2BLane'

const money = (v) => (v || v === 0 ? formatCurrency(v) : null)

/* ── merchant zone source tag (Default vs Overridden) ──────────────────────────
   Orders priced after the field shipped carry `merchantZoneSource` on the document.
   Older orders don't, so the tag is resolved live from the current base map + overrides
   via order/merchantZoneSource and marked as a live read. B2B orders have no tag. */
const TAG_BASE = { fontFamily: T.FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', borderRadius: 999, padding: '1px 8px', marginLeft: 8, verticalAlign: 'middle', whiteSpace: 'nowrap' }
const tagStyle = (color, bg, border) => ({ ...TAG_BASE, color, background: bg, border: `1px solid ${border}` })
const SOURCE_TAG = {
  DEFAULT: { label: 'Default', style: tagStyle(T.SUBTLE, T.LIGHT_BG, T.BORDER), title: 'Zone comes from the base merchant zone map.' },
  OVERRIDE: { label: 'Overridden', style: tagStyle(T.WARNING, T.WARNING_BG, '#F5D58A'), title: 'A merchant lane override supplied this zone (Merchant Setup › Zone Mapping › Zone Override).' },
}
const ZONE_VALUE_STYLE = { fontFamily: T.FONT, fontSize: 12, fontWeight: 600, color: T.NAVY }

function ZoneSourceTag({ source, live }) {
  const t = SOURCE_TAG[source]
  if (!t) return null
  const title = live ? `${t.title} Resolved just now from current mappings — this order predates source tracking.` : t.title
  return (
    <Tooltip title={title} arrow>
      <span style={t.style}>
        {t.label}{live ? ' · live' : ''}
      </span>
    </Tooltip>
  )
}

/** Stored source when present; else one live lookup for legacy (pre-field) orders. */
function useMerchantZoneSource(order) {
  const stored = order?.merchantZoneSource || null
  const isB2B = order?.shipment_type === 'B2B'
  const [live, setLive] = useState(null)
  useEffect(() => {
    setLive(null)
    if (!order?.id || stored || isB2B || !order?.merchantZone) return undefined
    let on = true
    getGeneric(apiConstants.ORDER_MERCHANT_ZONE_SOURCE, { orderId: order.id })
      .then((res) => {
        // GenericAPIResponse.success(...) → the payload is always under responseObj
        const body = res?.data?.responseObj || {}
        if (on && body.source) setLive(body.source)
      })
      .catch(() => {})
    return () => {
      on = false
    }
  }, [order?.id, stored, isB2B, order?.merchantZone])
  if (stored) return { source: stored, live: false }
  if (live) return { source: live, live: true }
  return { source: null, live: false }
}

export default function SummaryCards({ order }) {
  const zoneSource = useMerchantZoneSource(order)
  const lane = useB2BLane(order)
  if (!order) return null

  const sd = order.shipment_detail?.[0]
  const mp = order.merchant_pricing
  const isB2B = order.shipment_type === 'B2B'
  const paymentMode = order.payment_mode || ''

  const volWeight = sd && !isB2B && sd.item_length && sd.item_breadth && sd.item_height
    ? ((sd.item_length * sd.item_breadth * sd.item_height) / 5000).toFixed(2)
    : null

  const actualTat = order.actualDeliveryTat != null ? order.actualDeliveryTat : null
  const tatColor = actualTat != null && mp?.maxTat != null ? (actualTat <= mp.maxTat ? T.SUCCESS : T.ERROR) : T.NAVY

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={4}>
        <SectionCard title='Key Dates' sx={{ mb: 0, height: '100%' }}>
          <InfoRow label='Order Date' value={order.awbRegisteredDate ? parseDateAndTime(order.awbRegisteredDate) : null} />
          <InfoRow label='Pickup Date' value={order.actualPickUpTime ? formatDate(order.actualPickUpTime, true) : null} />
          <InfoRow label='Original EDD' value={order.edd ? formatDate(order.edd, true) : null} />
          <InfoRow label='Delivery Date' value={order.actualDeliveryTime ? formatDate(order.actualDeliveryTime, true) : null} />
          {mp?.minTat != null && mp?.maxTat != null && (
            <InfoRow label='Expected TAT' value={`${mp.minTat}–${mp.maxTat} days`} />
          )}
          {actualTat != null && (
            <InfoRow label='Actual TAT' value={<span style={{ fontFamily: T.FONT, fontSize: 12, fontWeight: 600, color: tatColor }}>{actualTat} day{actualTat !== 1 ? 's' : ''}</span>} />
          )}
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <SectionCard title='Shipping' sx={{ mb: 0, height: '100%' }}>
          <InfoRow label='Zone' value={lane ? (
            <>
              <Typography component='span' sx={{ display: 'block', fontFamily: T.FONT, fontSize: 12, color: T.NAVY, fontWeight: 600 }}>
                {lane.from.name} → {lane.to.name}
              </Typography>
              {(lane.from.area || lane.to.area) && (
                <Typography component='span' sx={{ display: 'block', fontFamily: T.FONT, fontSize: 12, color: T.SUBTLE, mt: 0.25 }}>
                  {lane.from.area || lane.from.name} → {lane.to.area || lane.to.name}
                </Typography>
              )}
              {lane.fromMap && (
                <Typography component='span' sx={{ display: 'block', fontFamily: T.FONT, fontSize: 12, color: T.SUBTLE, fontStyle: 'italic', mt: 0.25 }}>
                  From today’s zone map; not saved on this order
                </Typography>
              )}
            </>
          ) : order.merchantZone && !isB2B ? (
            <span style={ZONE_VALUE_STYLE}>
              {order.merchantZone}
              <ZoneSourceTag source={zoneSource.source} live={zoneSource.live} />
            </span>
          ) : order.merchantZone} />
          <InfoRow label='Dead Weight' value={sd?.item_weight ? `${(sd.item_weight / 1000).toFixed(2)} kg` : null} />
          <InfoRow label='Vol. Weight' value={volWeight ? `${volWeight} kg` : null} hide={isB2B} />
          <InfoRow label='Dimensions' value={sd?.item_length ? `${sd.item_length} x ${sd.item_breadth} x ${sd.item_height} cm` : null} hide={isB2B} />
          <InfoRow label='Courier Zone' value={order.courier_pricing?.zone} />
          <InfoRow label='Insured' value={order.isInsured ? 'Yes' : 'No'} />
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <SectionCard title='Financial' sx={{ mb: 0, height: '100%' }}>
          <InfoRow label='Invoice Value' value={money(order.invoice_value)} />
          <InfoRow label='Invoice #' value={order.invoice_number} />
          <InfoRow label='COD Amount' value={money(order.cod_amount)} hide={paymentMode !== 'COD'} />
          <InfoRow label='Forward Shipping Cost' value={money(mp?.price)} />
          <InfoRow label='RTO Shipping Cost' value={money(order.merchant_pricing_rto?.price)} hide={order.order_type === 'REVERSE SHIPMENT'} />
        </SectionCard>
      </Grid>
    </Grid>
  )
}
